import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * v1 used `focus:` (fires on mouse click too) with a `ring-brand/20` ring — a 20%-alpha ring on
 * a colour that already failed contrast. Focus now comes from the global `:focus-visible`
 * outline, and the resting border is `edge-control` at 3.24:1, which is what AA asks of a
 * control boundary.
 */
export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        "h-11 w-full rounded-sm border border-edge-control bg-surface px-3 text-body text-ink",
        "transition-colors duration-150 ease-standard placeholder:text-muted",
        "hover:border-ink-2 focus-visible:border-signal",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = "Input";

/** Label + field + optional error, so forms stop hand-rolling their own spacing. */
export function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
  className,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <label htmlFor={htmlFor} className="text-caption font-medium text-ink">
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-caption text-muted">{hint}</p>}
      {error && (
        <p className="text-caption text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
